import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from './common/Header';
import Footer from './common/Footer';

const NotFoundPage = () => {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/');
  };

  const handleGoBack = () => {
    window.history.back();
  };

  return (
    <div className="min-h-screen bg-page flex flex-col">
      <Header />
      
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-4xl mx-auto text-center">
          {/* 404 Hero Section */}
          <div className="mb-12">
            {/* Large 404 Text */}
            <div className="mb-8">
              <h1 className="text-9xl md:text-[12rem] font-bold text-transparent bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] bg-clip-text leading-none">
                404
              </h1>
              <div className="relative -mt-8">
                <div className="absolute inset-0 bg-gradient-to-r from-[#2f6eb0]/20 to-[#4c8fe0]/20 blur-3xl"></div>
              </div>
            </div>

            {/* Error Message */}
            <div className="mb-8">
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                Oops! Page Not Found
              </h2>
              <p className="text-xl text-white/80 mb-2">
                Looks like this content took a commercial break...
              </p>
              <p className="text-lg text-white/60">
                The page you're looking for doesn't exist or has been moved.
              </p>
            </div>

            {/* Fun TV-themed message */}
            <div className="bg-secondary/50 backdrop-blur-sm rounded-2xl p-6 mb-8 border border-[#2f6eb0]/30">
              <div className="flex items-center justify-center mb-4">
                <div className="w-16 h-16 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] rounded-full flex items-center justify-center">
                  <svg className="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h18v8zM9 10v4l4-2-4-2z"/>
                  </svg>
                </div>
              </div>
              <p className="text-white/90 text-lg">
                Don't worry! There's plenty of great content waiting for you.
              </p>
            </div>
          </div>

          {/* Navigation Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
            {/* Home */}
            <button
              onClick={handleGoHome}
              className="group bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] hover:from-[#4c8fe0] hover:to-[#2f6eb0] text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="flex flex-col items-center space-y-3">
                <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                </svg>
                <span className="font-semibold">Home</span>
                <span className="text-sm opacity-90">Browse featured content</span>
              </div>
            </button>

            {/* Live TV */}
            <button
              onClick={() => navigate('/live-tv')}
              className="group bg-secondary/80 hover:bg-secondary border border-[#2f6eb0]/30 hover:border-[#2f6eb0]/60 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="flex flex-col items-center space-y-3">
                <div className="relative">
                  <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M21 6H3c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H3V8h18v8z"/>
                  </svg>
                  <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                </div>
                <span className="font-semibold">Live TV</span>
                <span className="text-sm opacity-90">Watch live channels</span>
              </div>
            </button>

            {/* English */}
            <button
              onClick={() => navigate('/')}
              className="group bg-secondary/80 hover:bg-secondary border border-[#2f6eb0]/30 hover:border-[#2f6eb0]/60 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="flex flex-col items-center space-y-3">
                <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/>
                </svg>
                <span className="font-semibold">Home</span>
                <span className="text-sm opacity-90">Browse Home</span>
              </div>
            </button>

            {/* Español */}
            <button
              onClick={() => navigate('/')}
              className="group bg-secondary/80 hover:bg-secondary border border-[#2f6eb0]/30 hover:border-[#2f6eb0]/60 text-white rounded-xl p-6 transition-all duration-300 transform hover:scale-105 hover:shadow-2xl"
            >
              <div className="flex flex-col items-center space-y-3">
                <svg className="w-8 h-8 group-hover:scale-110 transition-transform" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12.87 15.07l-2.54-2.51.03-.03c1.74-1.94 2.98-4.17 3.71-6.53H17V4h-7V2H8v2H1v1.99h11.17C11.5 7.92 10.44 9.75 9 11.35 8.07 10.32 7.3 9.19 6.69 8h-2c.73 1.63 1.73 3.17 2.98 4.56l-5.09 5.02L4 19l5-5 3.11 3.11.76-2.04zM18.5 10h-2L12 22h2l1.12-3h4.75L21 22h2l-4.5-12zm-2.62 7l1.62-4.33L19.12 17h-3.24z"/>
                </svg>
                <span className="font-semibold">Español</span>
                <span className="text-sm opacity-90">Navegar en Español</span>
              </div>
            </button>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
            <button
              onClick={handleGoBack}
              className="px-8 py-3 bg-secondary/80 hover:bg-secondary border border-[#2f6eb0]/30 hover:border-[#2f6eb0]/60 text-white rounded-xl transition-all duration-300 transform hover:scale-105"
            >
              ← Go Back
            </button>
            <button
              onClick={handleGoHome}
              className="px-8 py-3 bg-gradient-to-r from-[#2f6eb0] to-[#4c8fe0] hover:from-[#4c8fe0] hover:to-[#2f6eb0] text-white rounded-xl transition-all duration-300 transform hover:scale-105 font-semibold"
            >
              Take Me Home
            </button>
          </div>

          {/* Additional Help */}
          <div className="mt-12 text-center">
            <p className="text-white/60 text-sm">
              Still having trouble? Try refreshing the page or check your internet connection.
            </p>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default NotFoundPage;

